import { useCallback, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

/**
 * 화면을 떠났다 돌아와도 남아 있는 state.
 *
 * **왜 필요한가** — 라우트가 바뀌면 컴포넌트가 언마운트되고 모든 `useState` 가 초기값으로
 * 돌아간다. 담당자가 짚은 상황은 이렇다: *"기사배차 입력하다가 잠깐 다른 화면 보고 싶어서
 * 갔다 오고 싶은데"* (2026-09-09). 쓰다 만 등록 폼이 통째로 날아가고, 조회 조건도 날짜는
 * 어제로 검색어는 빈 값으로 되돌아간다. 브라우저 `뒤로` 도 경로만 복원하고 컴포넌트는 새로
 * 마운트되므로 도움이 안 된다.
 *
 * 코드가 이 문제를 이미 알고 있었다 — `invoice/page.tsx` 는 일자만 URL 로 뺐고,
 * `dispatch/orders/page.tsx` 는 일자를 부모로 끌어올렸다. 둘 다 **그 화면 안에서만** 통한다.
 *
 * `useState` 와 시그니처가 같아 호출부는 한 줄만 바뀐다::
 *
 *     const [date, setDate] = useState(defaultDate());
 *     const [date, setDate] = useStickyState("dispatch.overview:date", defaultDate);
 *
 * **`localStorage` 가 아니라 모듈 전역 `Map` 이다.** 새로고침하면 사라지는 것이 맞다 —
 * 목적은 "잠깐 다른 화면 갔다 오기" 이지 어제 조건을 오늘 되살리는 것이 아니다.
 *
 * **보존하지 않는 것** — 모달 열림·선택된 행·확인 대기처럼 그 화면 안에서만 뜻이 있는 값,
 * 리마운트 열쇠(`draftKey`), 그리고 **평문 비밀번호가 든 초안**(기사 계정·사용자 관리·업체
 * 계정 발급). 세션 메모리에 남길 값이 아니다.
 */

/** 화면이 넘겨준 값. 타입은 호출부가 안다 */
const memory = new Map<string, unknown>();

/**
 * 값을 담아 둔 달력 날짜.
 *
 * 일 단위 화면의 기본값은 **어제**다(CLAUDE.md §UI 6). 탭을 밤새 켜 두면 `defaultDate()` 는
 * 하루 움직이는데 담아 둔 값은 안 움직여, 아침에 열면 **어제인 줄 알고 그제를 본다.**
 * 날이 바뀌면 통째로 비워 그 규칙이 조용히 깨지지 않게 한다.
 */
let storedOn = "";

function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

/** 날이 바뀌었으면 비운다. 읽고 쓰기 전에 언제나 먼저 부른다 */
function expireIfStale() {
  const now = today();
  if (storedOn === now) return;
  memory.clear();
  storedOn = now;
}

function read<T>(key: string): { hit: boolean; value?: T } {
  expireIfStale();
  return memory.has(key) ? { hit: true, value: memory.get(key) as T } : { hit: false };
}

export function useStickyState<T>(
  key: string,
  initial: T | (() => T)
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const stored = read<T>(key);
    if (stored.hit) return stored.value as T;
    return typeof initial === "function" ? (initial as () => T)() : initial;
  });

  const set = useCallback<Dispatch<SetStateAction<T>>>(
    (next) =>
      setValue((prev) => {
        const resolved =
          typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        expireIfStale();
        memory.set(key, resolved);
        return resolved;
      }),
    [key]
  );

  return [value, set];
}

/**
 * 접두어로 시작하는 값을 지운다.
 *
 * 등록에 성공했거나 담당자가 `등록 취소` 를 눌렀을 때 초안을 비우는 데 쓴다.
 * **폼을 리마운트하기 전에** 불러야 한다 — 순서가 뒤집히면 비운 직후 옛 값이 다시 복원된다.
 */
export function clearSticky(prefix: string) {
  for (const key of [...memory.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}:`) || key.startsWith(`${prefix}.`)) {
      memory.delete(key);
    }
  }
}

/**
 * 전부 지운다.
 *
 * 로그아웃에서 부른다 — 같은 브라우저를 쓰는 다음 사람이 앞사람의 조회 조건과 쓰다 만
 * 등록 폼을 물려받으면 안 된다.
 */
export function clearAllSticky() {
  memory.clear();
  storedOn = today();
}

export default useStickyState;
